import { isEmpty, isNil } from 'lodash';
import { treeNodeSubType } from '@/constants/tree';
import { getValueAfterCheckEmpty } from '@/utils/string';
import { isTrue } from '@/utils/boolean';

/**
 * 특정 노드의 Path상에 있는 모든 노드들을 depth 순서대로 넣은 배열을 반환한다.
 *
 * @param  {object} node 노드 객체
 * @return {arrawy}      path상에 있는 노드 객체 배열
 */
export function getPathNodes(node) {
  const pathNodes = [];
  let parent = node;
  while (parent && !Array.isArray(parent.data) && !isEmpty(parent.data)) {
    // Element Tree에서 root의 부모 요소로 최상위 root들의 배열을 가져오기 때문에 node.data가 배열형일 경우 추가하지 않음.
    pathNodes.unshift(parent);
    parent = parent.parent;
  }
  return pathNodes;
}

/**
 * 특정 노드의 Path상에 있는 모든 노드 데이터들을 depth 순서대로 넣은 배열을 반환한다.
 *
 * @param  {object} node 노드 객체
 * @return {arrawy}      path상에 있는 노드 데이터 객체 배열
 */
export function getPathNodeDatas(node) {
  const pathNodes = [];
  let parent = node;
  while (parent && !Array.isArray(parent.data) && !isEmpty(parent.data)) {
    pathNodes.unshift(parent.data);
    parent = parent.parent;
  }
  return pathNodes;
}

/**
 * 트리의 첫번째 단말 노드의 트리키를 찾아 반환한다.
 * @author Ju
 * @param  {[type]} node [description]
 * @return {String}      첫번째 단말 노드의 트리키
 */
export function findFirstLeafTreeKey(node) {
  if (node.childNodes && node.childNodes.length > 0) {
    const childNodes = node.childNodes;
    for (let i = 0; i < childNodes.length; i += 1) {
      if (childNodes[i].isLeaf) {
        return childNodes[i].data.treeKey;
      }
      const treeKey = findFirstLeafTreeKey(childNodes[i]);
      if (treeKey) {
        return treeKey;
      }
    }
  }
  return null;
}

/**
 * IP 정보 제공이 필요한 장비 구분인지 판단하여 그 결과를 반환한다.
 * IP 정보 제공이 필요한 경우 true, 아닌 경우 false를 반환
 * @author Ju
 * @param  {String}  type 노드 데이터 서브 타입
 * @return {Boolean}
 */
export function isRequireIPObjType(data) {
  const type = data.subType;
  if (treeNodeSubType.VIRTUAL === type) {
    return data.obj.treeNodeSubTypeId === 'VHO' || data.obj.treeNodeSubTypeId === 'VVM';
  }
  return treeNodeSubType.SERVICE !== type && treeNodeSubType.E2E !== type;
}

/**
 * 장비 구분에 해당하는 아이콘 클래스를 반환한다.
 * @author Ju
 * @param  {Object} data 노드 데이터 객체
 * @return {String, Object} 장비 구분별 아이콘 클래스명, 만약 없을 경우 null을 반환.
 */
export function getObjTypeIconClass(data) {
  const subType = data.subType;
  let iconClass;
  if (treeNodeSubType.SERVER === subType) {
    iconClass = 'fas fa-server';
  } else if (treeNodeSubType.NETDEVICE === subType) {
    if (isTrue(data.netdeviceFlowApplyFlag)) {
      iconClass = 'fas fa-random';
    } else {
      iconClass = 'fas fa-hdd'; // fas fa-digital-tachograph, fas fa-credit-card-front
    }
  } else if (treeNodeSubType.AP_CONTROLLER === subType) {
    iconClass = 'fad fa-hdd'; // TODO Ju 컨트롤러 / 무선 AP Flow 수집 여부 확인
  } else if (treeNodeSubType.WIRELESS_AP === subType) {
    iconClass = 'fad fa-wifi'; // TODO Ju 컨트롤러 / 무선 AP Flow 수집 여부 확인
  } else if (treeNodeSubType.NETINTERFACE === subType) {
    if (isTrue(data.netinterfaceFlowApplyFlag)) {
      iconClass = 'fas fa-random';
    } else {
      iconClass = 'fas fa-ethernet';
    }
  } else if (treeNodeSubType.DBMS === subType) {
    iconClass = 'fas fa-database';
  } else if (treeNodeSubType.SENSOR === subType) {
    iconClass = 'fas fa-wifi'; // fab fa-audible, fas fa-rss
  } else if (treeNodeSubType.E2E === subType) {
    iconClass = 'fas fa-desktop-alt';
  } else if (treeNodeSubType.SERVICE === subType) {
    iconClass = 'fas fa-cogs';
  } else if (treeNodeSubType.VIRTUAL === subType) {
    const treeNodeSubTypeId = data.obj ? data.obj.objtypeId : data.objtypeId;
    if (treeNodeSubTypeId === 'VVC') {
      iconClass = 'fas fa-clone';
    } else if (treeNodeSubTypeId === 'VDC') {
      iconClass = 'fas fa-building';
    } else if (treeNodeSubTypeId === 'VCL') {
      iconClass = 'fas fa-window-restore';
    } else if (treeNodeSubTypeId === 'VHO') {
      iconClass = 'fas fa-window-maximize';
    } else if (treeNodeSubTypeId === 'VVM') {
      iconClass = 'fas fa-cloud';
    } else if (treeNodeSubTypeId === 'VDSC') {
      iconClass = 'fas fa-coins';
    } else if (treeNodeSubTypeId === 'VDS') {
      iconClass = 'fas fa-hockey-puck';
    }
  } else if (treeNodeSubType.APM === subType) {
    iconClass = 'fas fa-browser';
  } else {
    iconClass = null;
  }
  return iconClass;
}

/**
 * 관리대상 노드의 라벨을 반환한다.
 * @author Ju
 * @param  {Object} data
 * @return {String}
 */
export function getObjNodeLabel(data) {
  let label;
  if (isRequireIPObjType(data.subType)) {
    label = `${data.label} (${getValueAfterCheckEmpty(data.ipaddress)})`;
  } else {
    label = data.label;
  }
  return label;
}

/**
 * IP 툴팁에 대한 컨텐츠를 반환한다.
 * @author Ju
 * @param  {Object} data
 * @return {String}
 */
export function getIpTooltipContent(data) {
  if (isNil(data.ipaddress)) {
    return getValueAfterCheckEmpty(data.parentIpaddress);
  }
  return getValueAfterCheckEmpty(data.ipaddress);
}

/**
 * 관리 대상 노드인지 여부를 반환한다.
 * @author Ju
 * @param  {Object}  data
 * @return {Boolean}
 */
export function isObjNode(data) {
  return data.type === 'obj';
}

export function hasObjTypeIcon(data) {
  return !isEmpty(data.subType);
}

/**
 * 관리 대상에 맞는 subType을 반환한다.
 * (java → TreeUtil.getSubType과 동일한 기능.)
 * @author Ju
 * @param  {String} managetypeId 관리구분 아이디
 * @param  {String} objDefineId  장비 정의 아이디
 * @param  {String} objtypeId 장비 구분 아이디 (option)
 * @return {[type]}              [description]
 */
export function getSubTypeByObjInfo(managetypeId, objDefineId, objtypeId) {
  let subType = null;
  if (managetypeId === 'SMS ') {
    subType = treeNodeSubType.SERVER;
  } else if (managetypeId === 'NMS') {
    if (objDefineId === 'NETDEVICE') {
      subType = treeNodeSubType.NETDEVICE;
    } else if (objDefineId === 'WIRELESS_DEVICE') {
      if (objtypeId === 'WIRELESSAP') {
        subType = treeNodeSubType.WIRELESS_AP;
      } else {
        subType = treeNodeSubType.AP_CONTROLLER;
      }
    } else {
      subType = treeNodeSubType.NETINTERFACE;
    }
  } else if (managetypeId === 'E2E') {
    subType = treeNodeSubType.E2E;
  } else if (managetypeId === 'DBMS') {
    subType = treeNodeSubType.DBMS;
  } else if (managetypeId === 'FMS') {
    subType = treeNodeSubType.SENSOR;
  } else if (managetypeId === 'SVC') {
    subType = treeNodeSubType.SERVICE;
  } else if (managetypeId === 'APM2X') {
    subType = treeNodeSubType.APM;
  } else if (managetypeId === 'VMS') {
    subType = treeNodeSubType.VIRTUAL;
  }
  return subType;
}

export default {
  getPathNodes,
  getPathNodeDatas,
};



// WEBPACK FOOTER //
// ./src/utils/tree.js